import { useState } from "react";
import {
  Modal,
  Button,
  Input,
  List,
  Avatar,
  Spin,
  Tag,
  Collapse,
  Timeline,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  MessageSquare,
  Send,
  Trash2,
  Bell,
  History,
  Calendar,
  User,
  Repeat,
} from "lucide-react";
import dayjs from "dayjs";

const STATUS_COLORS = {
  PENDING: "orange",
  IN_PROGRESS: "blue",
  COMPLETED: "green",
  CANCELLED: "default",
};

const STATUS_LABELS = {
  PENDING: "Pendiente",
  IN_PROGRESS: "En Progreso",
  COMPLETED: "Completada",
  CANCELLED: "Cancelada",
};

const PRIORITY_COLORS = {
  LOW: "green",
  MEDIUM: "orange",
  HIGH: "red",
};

const PRIORITY_LABELS = {
  LOW: "Baja",
  MEDIUM: "Media",
  HIGH: "Alta",
};

const ACTION_LABELS = {
  CREATED: "Creada",
  UPDATED: "Actualizada",
  STATUS_CHANGED: "Cambio de estado",
  ARCHIVED: "Archivada",
  RESTORED: "Restaurada",
};

function TaskDetailModal({
  visible,
  task,
  onClose,
  onEdit,
  onAddComment,
  onDeleteComment,
  loading,
}) {
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!task) return null;

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    await onAddComment(newComment);
    setNewComment("");
    setSubmitting(false);
  };

  const formatChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) return null;

    const fieldLabels = {
      title: "Titulo",
      description: "Descripcion",
      date: "Fecha",
      status: "Estado",
      priority: "Prioridad",
      color: "Color",
      hasReminder: "Recordatorio",
      isArchived: "Archivada",
    };

    return Object.entries(changes).map(([field, { old: oldVal, new: newVal }]) => {
      const label = fieldLabels[field] || field;

      // Formatear valores especiales
      if (field === "status") {
        oldVal = STATUS_LABELS[oldVal] || oldVal;
        newVal = STATUS_LABELS[newVal] || newVal;
      } else if (field === "priority") {
        oldVal = PRIORITY_LABELS[oldVal] || oldVal;
        newVal = PRIORITY_LABELS[newVal] || newVal;
      } else if (field === "hasReminder" || field === "isArchived") {
        oldVal = oldVal ? "Si" : "No";
        newVal = newVal ? "Si" : "No";
      }

      return (
        <div key={field} className="text-sm text-gray-500">
          <strong>{label}:</strong> {oldVal || "(vacio)"} → {newVal || "(vacio)"}
        </div>
      );
    });
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: task.color }}
          />
          {task.isRecurring && (
            <Tooltip title="Tarea periodica">
              <Repeat size={16} className="text-blue-500" />
            </Tooltip>
          )}
          <span className="truncate">{task.title}</span>
          <Tag color={STATUS_COLORS[task.status]}>{STATUS_LABELS[task.status]}</Tag>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={
        <div className="flex justify-between">
          <Button onClick={onClose}>Cerrar</Button>
          <Button type="primary" onClick={onEdit}>
            Editar
          </Button>
        </div>
      }
      width={650}
    >
      <Spin spinning={loading?.get}>
        {/* Informacion principal */}
        <div className="mb-4 pb-4 border-b">
          <div className="flex flex-wrap gap-4 mb-3">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-gray-400" />
              <span>
                {task.date
                  ? dayjs(task.date).format("DD/MM/YYYY")
                  : "Sin fecha"}
              </span>
            </div>
            <Tag color={PRIORITY_COLORS[task.priority]}>
              Prioridad: {PRIORITY_LABELS[task.priority]}
            </Tag>
            {task.hasReminder && (
              <Tag icon={<Bell size={12} />} color="warning">
                Con recordatorio
              </Tag>
            )}
          </div>

          {task.description && (
            <p className="text-gray-600 whitespace-pre-wrap">{task.description}</p>
          )}

          <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-500">
            <p>
              Creada por <strong>{task.createdBy?.firstName || "Usuario"}</strong> el{" "}
              {dayjs(task.createdAt).format("DD/MM/YYYY HH:mm")}
            </p>
            {task.assignedTo && (
              <p className="flex items-center gap-1">
                <User size={14} />
                Asignada a <strong>{task.assignedTo.firstName || task.assignedTo.email}</strong>
              </p>
            )}
          </div>
        </div>

        {/* Comentarios */}
        <div className="mb-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <MessageSquare size={18} />
            Comentarios ({task.comments?.length || 0})
          </h4>

          <List
            dataSource={task.comments || []}
            locale={{ emptyText: "Sin comentarios" }}
            renderItem={(comment) => (
              <List.Item
                actions={[
                  <Popconfirm
                    title="Eliminar comentario?"
                    onConfirm={() => onDeleteComment(comment.id)}
                    key="delete"
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<Trash2 size={14} />}
                    />
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar style={{ backgroundColor: "#1890ff" }}>
                      {comment.createdBy?.firstName?.[0] || "U"}
                    </Avatar>
                  }
                  title={
                    <span className="text-sm">
                      {comment.createdBy?.firstName || "Usuario"}
                      <span className="text-gray-400 ml-2 font-normal">
                        {dayjs(comment.createdAt).format("DD/MM HH:mm")}
                      </span>
                    </span>
                  }
                  description={
                    <p className="whitespace-pre-wrap">{comment.content}</p>
                  }
                />
              </List.Item>
            )}
          />

          <div className="flex gap-2 mt-4">
            <Input.TextArea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escribe un comentario..."
              rows={2}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleAddComment();
                }
              }}
            />
            <Button
              type="primary"
              icon={<Send size={16} />}
              onClick={handleAddComment}
              loading={submitting}
            >
              Enviar
            </Button>
          </div>
        </div>

        {/* Historial */}
        {task.history && task.history.length > 0 && (
          <Collapse
            ghost
            items={[
              {
                key: "history",
                label: (
                  <span className="flex items-center gap-2">
                    <History size={16} />
                    Historial de cambios ({task.history.length})
                  </span>
                ),
                children: (
                  <Timeline
                    items={task.history.map((entry) => ({
                      color:
                        entry.action === "CREATED"
                          ? "green"
                          : entry.action === "ARCHIVED"
                          ? "red"
                          : "blue",
                      children: (
                        <div>
                          <div className="font-medium">
                            {ACTION_LABELS[entry.action] || entry.action}
                          </div>
                          {formatChanges(entry.changes)}
                          <div className="text-xs text-gray-400 mt-1">
                            {entry.user?.firstName} -{" "}
                            {dayjs(entry.createdAt).format("DD/MM/YYYY HH:mm")}
                          </div>
                        </div>
                      ),
                    }))}
                  />
                ),
              },
            ]}
          />
        )}
      </Spin>
    </Modal>
  );
}

export default TaskDetailModal;
